import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  CardActions,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  DirectionsBoat as MarinaIcon,
  Storefront as StoreIcon,
  Restaurant as KitchenIcon,
  Business as BusinessIcon,
  People as StaffIcon,
  BarChart as ReportsIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { collection, query, getDocs, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format } from 'date-fns';

// Interface for dashboard data
interface DashboardData {
  customers: number;
  staff: number;
  vessels: number;
  weeklyRevenue: number;
  marinaReservations: number;
  marinaRevenue: number;
  storeOrders: number;
  storeRevenue: number;
  kitchenOrders: number;
  kitchenRevenue: number;
  alerts: Array<{
    title: string;
    date: string;
    type: 'primary' | 'warning' | 'error' | 'info' | 'success';
  }>;
}

const BusinessDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    customers: 0,
    staff: 0,
    vessels: 0,
    weeklyRevenue: 0,
    marinaReservations: 0,
    marinaRevenue: 0,
    storeOrders: 0,
    storeRevenue: 0,
    kitchenOrders: 0,
    kitchenRevenue: 0,
    alerts: []
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get current date for time-based queries
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        // Initialize data object
        const data: Partial<DashboardData> = {
          customers: 0,
          staff: 0,
          vessels: 0,
          weeklyRevenue: 0,
          marinaReservations: 0,
          marinaRevenue: 0,
          storeOrders: 0,
          storeRevenue: 0,
          kitchenOrders: 0,
          kitchenRevenue: 0,
          alerts: []
        };
        
        // Count customers - handled separately with try/catch
        try {
          const customersSnapshot = await getDocs(collection(db, 'customers'));
          data.customers = customersSnapshot.size;
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Fetched customers:', data.customers);
          // }
        } catch (err) {
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Error fetching customers data:', err);
          // }
          // Continue with other data
        }
        
        // Count staff members - handled separately with try/catch
        try {
          const staffSnapshot = await getDocs(collection(db, 'staff'));
          data.staff = staffSnapshot.size;
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Fetched staff:', data.staff);
          // }
        } catch (err) {
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Error fetching staff data:', err);
          // }
          // Continue with other data
        }
        
        // Count vessels - handled separately with try/catch
        try {
          const vesselsSnapshot = await getDocs(collection(db, 'vessels'));
          data.vessels = vesselsSnapshot.size;
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Fetched vessels:', data.vessels);
          // }
        } catch (err) {
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Error fetching vessels data:', err);
          // }
          // Continue with other data
        }
        
        // Initialize arrays for data we'll collect
        let reservations: any[] = [];
        let orders: any[] = [];
        let payments: any[] = [];
        let maintenanceAlerts: any[] = [];

        // Get recent reservations (marina) - handled separately with try/catch
        try {
          const recentReservationsQuery = query(
            collection(db, 'reservations'),
            where('startDate', '>=', Timestamp.fromDate(oneWeekAgo)),
            orderBy('startDate', 'desc')
          );
          const reservationsSnapshot = await getDocs(recentReservationsQuery);
          reservations = reservationsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Fetched reservations:', reservations.length);
          // }
        } catch (err) {
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Error fetching reservations data:', err);
          // }
          // Continue with other data
        }
        
        // Get recent orders (store & kitchen) - handled separately with try/catch
        try {
          const recentOrdersQuery = query(
            collection(db, 'orders'),
            where('createdAt', '>=', Timestamp.fromDate(oneWeekAgo)),
            orderBy('createdAt', 'desc')
          );
          const ordersSnapshot = await getDocs(recentOrdersQuery);
          orders = ordersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Fetched orders:', orders.length);
          // }
        } catch (err) {
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Error fetching orders data:', err);
          // }
          // Continue with other data
        }
        
        // Get recent payments - handled separately with try/catch
        try {
          const recentPaymentsQuery = query(
            collection(db, 'payments'),
            where('createdAt', '>=', Timestamp.fromDate(oneWeekAgo)),
            orderBy('createdAt', 'desc')
          );
          const paymentsSnapshot = await getDocs(recentPaymentsQuery);
          payments = paymentsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Fetched payments:', payments.length);
          // }
        } catch (err) {
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Error fetching payments data:', err);
          // }
          // Continue with other data
        }
        
        // Get maintenance alerts - handled separately with try/catch
        try {
          const maintenanceQuery = query(
            collection(db, 'maintenance'),
            where('status', '==', 'pending'),
            orderBy('dueDate', 'asc'),
            limit(5)
          );
          const maintenanceSnapshot = await getDocs(maintenanceQuery);
          maintenanceAlerts = maintenanceSnapshot.docs.map(doc => {
            const docData = doc.data();
            return {
              title: docData.title || 'Maintenance Required',
              date: docData.dueDate ? format(docData.dueDate.toDate(), 'MMMM d, yyyy') : 'No due date',
              type: 'warning' as const
            };
          });
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Fetched maintenance alerts:', maintenanceAlerts.length);
          // }
        } catch (err) {
          // if (process.env.NODE_ENV === 'development') {
          //   console.log('Error fetching maintenance data:', err);
          // }
          // Continue with other data
        }
        
        // Calculate totals based on what we have
        data.marinaReservations = reservations.length;
        data.marinaRevenue = reservations.reduce((total, res: any) => {
          return total + (res.totalPrice ? Number(res.totalPrice) : 0);
        }, 0);
        
        // Filter orders by type
        data.storeOrders = orders.filter((order: any) => order.type === 'RETAIL').length;
        data.kitchenOrders = orders.filter((order: any) => 
          order.type === 'DINE_IN' || order.type === 'TAKEOUT'
        ).length;
        
        // Calculate revenue by type
        data.storeRevenue = orders
          .filter((order: any) => order.type === 'RETAIL')
          .reduce((total, order: any) => total + (order.total ? Number(order.total) : 0), 0);
        
        data.kitchenRevenue = orders
          .filter((order: any) => order.type === 'DINE_IN' || order.type === 'TAKEOUT')
          .reduce((total, order: any) => total + (order.total ? Number(order.total) : 0), 0);
        
        // Calculate total weekly revenue
        data.weeklyRevenue = payments.reduce((total, payment: any) => {
          const amount = payment.amount || 
                        (payment.amountMoney ? payment.amountMoney.amount / 100 : 0);
          return total + Number(amount);
        }, 0);
        
        // Combine maintenance alerts with upcoming events
        const upcomingEvents = [
          {
            title: 'Staff Meeting',
            date: format(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 3), 'MMMM d, yyyy'),
            type: 'primary' as const
          },
          {
            title: 'Inventory Count',
            date: format(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7), 'MMMM d, yyyy'),
            type: 'info' as const
          }
        ];
        
        // Sort alerts by date
        data.alerts = [...maintenanceAlerts, ...upcomingEvents]
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        
        // Update dashboard data
        setDashboardData(data as DashboardData);
        // if (process.env.NODE_ENV === 'development') {
        //   console.log('Dashboard data updated successfully');
        // }
        
      } catch (err) {
        // if (process.env.NODE_ENV === 'development') {
        //   console.error('Error fetching dashboard data:', err);
        // }
        setError('Failed to load some dashboard data. Some information may be incomplete.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get safe values with fallbacks for missing data
  const getSafeValue = (value: number | undefined): number => {
    return typeof value === 'number' ? value : 0;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Fallen Leaf Marina Business Administration
      </Typography>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Welcome, {userProfile?.firstName || 'Admin'}
      </Typography>
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* Business overview */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Business Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h3">{getSafeValue(dashboardData.customers)}</Typography>
                    <Typography variant="subtitle1">Total Customers</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', bgcolor: 'secondary.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h3">{getSafeValue(dashboardData.staff)}</Typography>
                    <Typography variant="subtitle1">Staff Members</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h3">{formatCurrency(getSafeValue(dashboardData.weeklyRevenue))}</Typography>
                    <Typography variant="subtitle1">Weekly Revenue</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Department Performance
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MarinaIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">Marina</Typography>
                  </Box>
                  <Typography variant="body2">Revenue: {formatCurrency(getSafeValue(dashboardData.marinaRevenue))}</Typography>
                  <Typography variant="body2">Reservations: {getSafeValue(dashboardData.marinaReservations)}</Typography>
                  <Typography variant="body2">Vessels: {getSafeValue(dashboardData.vessels)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <StoreIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">Store</Typography>
                  </Box>
                  <Typography variant="body2">Revenue: {formatCurrency(getSafeValue(dashboardData.storeRevenue))}</Typography>
                  <Typography variant="body2">Sales: {getSafeValue(dashboardData.storeOrders)}</Typography>
                  <Typography variant="body2">Utilization: {Math.round((getSafeValue(dashboardData.storeRevenue) / (getSafeValue(dashboardData.weeklyRevenue) || 1)) * 100)}%</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <KitchenIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">Kitchen</Typography>
                  </Box>
                  <Typography variant="body2">Revenue: {formatCurrency(getSafeValue(dashboardData.kitchenRevenue))}</Typography>
                  <Typography variant="body2">Orders: {getSafeValue(dashboardData.kitchenOrders)}</Typography>
                  <Typography variant="body2">Utilization: {Math.round((getSafeValue(dashboardData.kitchenRevenue) / (getSafeValue(dashboardData.weeklyRevenue) || 1)) * 100)}%</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Important alerts */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Business Alerts
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {dashboardData.alerts && dashboardData.alerts.length > 0 ? (
                dashboardData.alerts.map((alert, index) => (
                  <ListItem key={index} divider={index < dashboardData.alerts.length - 1}>
                    <ListItemText 
                      primary={alert.title} 
                      secondary={alert.date} 
                      primaryTypographyProps={{ color: `${alert.type}.main` }}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText 
                    primary="No Alerts" 
                    secondary="You're all caught up!" 
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
        
        {/* Department cards */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Department Management
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MarinaIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h5">Marina</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Manage vessels, reservations, and marina operations. View dock status and upcoming bookings.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button component={Link} to="/admin/dashboard" size="small" color="primary">
                    Go to Marina Admin
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <StoreIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h5">Store</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Manage store inventory, products, sales, and promotions. Track stock levels and top-selling items.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button component={Link} to="/admin/store/dashboard" size="small" color="primary">
                    Go to Store Admin
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <KitchenIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h5">Kitchen</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Manage menu items, food inventory, and kitchen orders. Track popular dishes and ingredient levels.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button component={Link} to="/admin/kitchen/dashboard" size="small" color="primary">
                    Go to Kitchen Admin
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        
        {/* Business management tools */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Business Management
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Card component={Link} to="/admin/business/locations" sx={{ textAlign: 'center', textDecoration: 'none' }}>
                <CardContent>
                  <BusinessIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="subtitle1" color="primary">
                    Company Info
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card component={Link} to="/admin/staff" sx={{ textAlign: 'center', textDecoration: 'none' }}>
                <CardContent>
                  <StaffIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="subtitle1" color="primary">
                    Staff Directory
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card component={Link} to="/admin/business/reports/overview" sx={{ textAlign: 'center', textDecoration: 'none' }}>
                <CardContent>
                  <ReportsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="subtitle1" color="primary">
                    Business Reports
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card component={Link} to="/admin/business/settings/general" sx={{ textAlign: 'center', textDecoration: 'none' }}>
                <CardContent>
                  <SettingsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="subtitle1" color="primary">
                    Global Settings
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BusinessDashboard; 